import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Budget() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ category: "Textbooks", department: "Academics", allocated: "" });
  const load = useCallback(() => api.get("/budgets").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", { ...f, allocated: parseFloat(f.allocated) });
      toast.success("Budget line added");
      setF({ category: "Textbooks", department: "Academics", allocated: "" });
      load();
    } catch (err) { toast.error("Failed to add budget"); }
  };
  const totalAlloc = items.reduce((s, b) => s + b.allocated, 0);
  const totalUsed = items.reduce((s, b) => s + (b.utilized || 0), 0);
  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Budget Management">
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="sl-card p-5"><div className="text-xs uppercase text-slate-500 font-semibold">Annual Allocated</div><div className="sl-stat-value mt-2">₹{totalAlloc.toLocaleString('en-IN')}</div></div>
        <div className="sl-card p-5"><div className="text-xs uppercase text-slate-500 font-semibold">Utilized</div><div className="sl-stat-value mt-2">₹{totalUsed.toLocaleString('en-IN')}</div></div>
        <div className="sl-card p-5"><div className="text-xs uppercase text-slate-500 font-semibold">Remaining</div><div className="sl-stat-value mt-2 text-brand-green">₹{Math.max(0, totalAlloc - totalUsed).toLocaleString('en-IN')}</div></div>
      </div>

      <form onSubmit={add} className="sl-card p-5 mb-6 grid md:grid-cols-4 gap-3">
        <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} data-testid="bud-category" className="rounded-md border border-slate-300 px-3 py-2">
          {["Textbooks","Library Books","Stationery","Uniforms","Shoes","Laboratory Equipment","Sports Equipment","Furniture","IT Equipment","STEM Kits"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={f.department} onChange={(e) => setF({...f, department: e.target.value})} data-testid="bud-department" placeholder="Department" className="rounded-md border border-slate-300 px-3 py-2" />
        <input required type="number" min="0" value={f.allocated} onChange={(e) => setF({...f, allocated: e.target.value})} data-testid="bud-amount" placeholder="Allocated (₹)" className="rounded-md border border-slate-300 px-3 py-2" />
        <button data-testid="bud-add" className="rounded-md bg-brand-blue text-white font-semibold px-4 py-2 hover:bg-brand-blueDark transition-colors duration-200 flex items-center justify-center gap-2"><Plus size={16} /> Add Budget</button>
      </form>

      <div className="sl-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Department</th><th className="text-right px-4 py-3">Allocated</th><th className="text-right px-4 py-3">Utilized</th><th className="text-right px-4 py-3">Remaining</th><th className="text-left px-4 py-3">Progress</th></tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const pct = Math.min(100, Math.round(((b.utilized || 0) / (b.allocated || 1)) * 100));
              return (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-800">{b.category}</td>
                  <td className="px-4 py-3 text-slate-600">{b.department}</td>
                  <td className="px-4 py-3 text-right">₹{b.allocated.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right">₹{(b.utilized || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right text-brand-green font-semibold">₹{(b.remaining ?? b.allocated).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3"><div className="h-2 bg-slate-100 rounded-full overflow-hidden w-40"><div className="h-full bg-brand-blue" style={{ width: `${pct}%` }} /></div></td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">No budgets yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
